import { z } from 'zod';

/**
 * THE REFERENCES LIBRARY CONTRACT (design: docs/superpowers/specs/2026-11-02-workspace-references-design.md).
 *
 * One file for the API, the SPA and the tests, so the library's vocabulary, its
 * limits and the shape of every answer cannot drift between the three.
 *
 * A REFERENCE IS STILL NOT A CANDIDATE. Linking one to a place, a scene or an
 * element records an association and changes nothing about that item: no
 * shortlist, no approval, no booking, no availability, no scene status.
 */

/**
 * Where the material came from. Stored in `script_breakdown_project_reference.type`,
 * whose historical values are kept: a LINK is stored as `URL`, and a legacy `IMAGE`
 * reads as FILE. LOCATION is the pre-library "saved catalogue location" and is
 * read-only legacy: nothing creates one any more.
 */
export const ReferenceSourceType = z.enum(['LINK', 'FILE', 'NOTE', 'LOCATION']);
export type ReferenceSourceType = z.infer<typeof ReferenceSourceType>;

/** The kinds a person can create. */
export const CreatableReferenceSource = z.enum(['LINK', 'NOTE']);
export type CreatableReferenceSource = z.infer<typeof CreatableReferenceSource>;

/**
 * What the material is FOR, independent of where it came from: a PDF can be a
 * wardrobe reference and a Vimeo link overall mood. Optional; NULL is "not said".
 */
export const ReferenceCategory = z.enum([
  'INSPIRATION',
  'LOCATION',
  'SET_DESIGN',
  'WARDROBE',
  'PROPS',
  'VEHICLES_EQUIPMENT',
  'RESEARCH',
  'OTHER',
]);
export type ReferenceCategory = z.infer<typeof ReferenceCategory>;

export const REFERENCE_CATEGORY_LABELS: Readonly<Record<ReferenceCategory, string>> = Object.freeze({
  INSPIRATION: 'Inspirational',
  LOCATION: 'Location reference',
  SET_DESIGN: 'Set reference',
  WARDROBE: 'Wardrobe',
  PROPS: 'Props',
  VEHICLES_EQUIPMENT: 'Vehicles & equipment',
  RESEARCH: 'Research',
  OTHER: 'Other',
});

export const REFERENCE_SOURCE_LABELS: Readonly<Record<ReferenceSourceType, string>> = Object.freeze({
  LINK: 'Link',
  FILE: 'File',
  NOTE: 'Note',
  LOCATION: 'Saved location',
});

/**
 * What a reference can be linked to — the same four production items a
 * conversation can be anchored to, validated by the same `AnchorResolver`:
 * a scene of this production, a script location (requirement), a place (a
 * `location.id` with a live candidate here) and a breakdown element.
 */
export const ReferenceLinkTargetType = z.enum(['SCENE', 'REQUIREMENT', 'PLACE', 'ELEMENT']);
export type ReferenceLinkTargetType = z.infer<typeof ReferenceLinkTargetType>;

export const REFERENCE_LINK_TARGET_LABELS: Readonly<Record<ReferenceLinkTargetType, string>> = Object.freeze({
  SCENE: 'Scene',
  REQUIREMENT: 'Script location',
  PLACE: 'Place',
  ELEMENT: 'Production element',
});

/**
 * The preview the server made by RE-ENCODING an image (owner decision
 * 2026-11-02): an uploaded image, a video frame the browser took, or a link's
 * preview image fetched server-side. Originals and documents are never served.
 */
export const ReferencePreviewStatus = z.enum(['NONE', 'READY', 'FAILED', 'UNSUPPORTED']);
export type ReferencePreviewStatus = z.infer<typeof ReferencePreviewStatus>;

export const REFERENCE_TITLE_MAX = 255;
export const REFERENCE_DESCRIPTION_MAX = 4000;
export const REFERENCE_NOTE_MAX = 20000;
export const REFERENCE_URL_MAX = 1024;
export const REFERENCE_GROUP_NAME_MAX = 80;
export const REFERENCE_TAG_MAX = 40;
export const REFERENCE_TAGS_PER_REFERENCE_MAX = 30;
export const REFERENCE_GROUPS_PER_REFERENCE_MAX = 20;
export const REFERENCE_LINKS_PER_REFERENCE_MAX = 50;
export const REFERENCE_PAGE_SIZE_MAX = 60;

/** Lower-cased, inner whitespace collapsed: "Natural  Light" and "natural light" are one tag. */
export function referenceTagKey(label: string): string {
  return label.trim().replace(/\s+/g, ' ').toLowerCase();
}

/** The same rule for group names, so two live groups cannot differ only by case or spacing. */
export function referenceGroupKey(name: string): string {
  return referenceTagKey(name);
}

/** Every write that creates something carries a client id, so a retried request makes nothing twice. */
const ClientRequestId = z.uuid();

const Title = z.string().trim().max(REFERENCE_TITLE_MAX);
const Description = z.string().trim().max(REFERENCE_DESCRIPTION_MAX);
const TagLabel = z.string().trim().min(1).max(REFERENCE_TAG_MAX);

export const ReferenceLinkInput = z.object({
  targetType: ReferenceLinkTargetType,
  targetId: z.string().min(1).max(26),
});
export type ReferenceLinkInput = z.infer<typeof ReferenceLinkInput>;

/**
 * What can be applied to one reference at creation, and to every file of a bulk
 * upload: groups, tags, links and a category. Each successfully created
 * reference gets all of it; editing them one by one afterwards is the detail panel.
 */
export const ReferenceSharedMetadata = z.object({
  category: ReferenceCategory.nullable().optional(),
  groupIds: z.array(z.string().length(26)).max(REFERENCE_GROUPS_PER_REFERENCE_MAX).default([]),
  tags: z.array(TagLabel).max(REFERENCE_TAGS_PER_REFERENCE_MAX).default([]),
  links: z.array(ReferenceLinkInput).max(REFERENCE_LINKS_PER_REFERENCE_MAX).default([]),
});
export type ReferenceSharedMetadata = z.infer<typeof ReferenceSharedMetadata>;

/** `POST /v1/projects/:projectId/references` — a link or a note. Files go through `/references/files`. */
export const CreateReferenceInput = ReferenceSharedMetadata.extend({
  clientRequestId: ClientRequestId,
  sourceType: CreatableReferenceSource,
  url: z.string().trim().max(REFERENCE_URL_MAX).optional(),
  title: Title.optional(),
  description: Description.optional(),
  note: z.string().max(REFERENCE_NOTE_MAX).optional(),
});
export type CreateReferenceInput = z.infer<typeof CreateReferenceInput>;

/**
 * `POST /v1/projects/:projectId/references/files` is multipart: the `file` part,
 * an optional `poster` part (a frame the browser took from a video, JPEG or PNG),
 * and a `metadata` field holding this object as JSON.
 */
export const UploadReferenceMetadata = ReferenceSharedMetadata.extend({
  clientRequestId: ClientRequestId,
  title: Title.optional(),
  description: Description.optional(),
});
export type UploadReferenceMetadata = z.infer<typeof UploadReferenceMetadata>;

/** `PATCH /v1/projects/:projectId/references/:referenceId` — compare-and-swap on `rowVersion`. */
export const UpdateReferenceInput = z.object({
  expectedVersion: z.number().int().min(0),
  title: Title.optional(),
  description: Description.nullable().optional(),
  note: z.string().max(REFERENCE_NOTE_MAX).nullable().optional(),
  url: z.string().trim().max(REFERENCE_URL_MAX).optional(),
  category: ReferenceCategory.nullable().optional(),
});
export type UpdateReferenceInput = z.infer<typeof UpdateReferenceInput>;

export const SetReferenceGroupsInput = z.object({
  groupIds: z.array(z.string().length(26)).max(REFERENCE_GROUPS_PER_REFERENCE_MAX),
});
export const SetReferenceTagsInput = z.object({
  tags: z.array(TagLabel).max(REFERENCE_TAGS_PER_REFERENCE_MAX),
});
export const LinkPreviewInput = z.object({ url: z.string().trim().min(1).max(REFERENCE_URL_MAX) });

export const CreateReferenceGroupInput = z.object({
  clientRequestId: ClientRequestId,
  name: z.string().trim().min(1).max(REFERENCE_GROUP_NAME_MAX),
});
export const RenameReferenceGroupInput = z.object({
  name: z.string().trim().min(1).max(REFERENCE_GROUP_NAME_MAX),
});
export const AddToReferenceGroupInput = z.object({
  referenceIds: z.array(z.string().length(26)).min(1).max(REFERENCE_PAGE_SIZE_MAX),
});

export const ReferenceSort = z.enum(['RECENT', 'UPDATED', 'TITLE']);
export type ReferenceSort = z.infer<typeof ReferenceSort>;

/** `GET /v1/projects/:projectId/references` query. Every filter combines with AND. */
export const ReferenceListQuery = z.object({
  q: z.string().trim().max(200).optional(),
  group: z.string().length(26).optional(),
  source: ReferenceSourceType.optional(),
  category: ReferenceCategory.optional(),
  /** Linked to anything of this type; with `linkedId`, to that one item. `ANY` = linked to something. */
  linked: z.union([ReferenceLinkTargetType, z.literal('ANY')]).optional(),
  linkedId: z.string().min(1).max(26).optional(),
  tag: z.string().trim().max(REFERENCE_TAG_MAX).optional(),
  discussed: z.enum(['1']).optional(),
  sort: ReferenceSort.default('RECENT'),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(REFERENCE_PAGE_SIZE_MAX).default(30),
});
export type ReferenceListQuery = z.infer<typeof ReferenceListQuery>;

export interface ReferencePerson {
  readonly userId: number;
  /** From the user directory; null when it could not be read. */
  readonly displayName: string | null;
}

export interface ReferencePreview {
  readonly status: ReferencePreviewStatus;
  /** Short-lived signed URL of the re-encoded small image (cards), when READY. */
  readonly thumbUrl: string | null;
  /** Short-lived signed URL of the re-encoded large image (detail), when READY and asked for. */
  readonly largeUrl: string | null;
  readonly width: number | null;
  readonly height: number | null;
}

export interface ReferenceFileFacts {
  readonly originalFilename: string;
  readonly mimeType: string;
  readonly byteSize: number;
  /** IMAGE | VIDEO | PDF | DOCUMENT | OTHER — what the icon shows. */
  readonly kind: 'IMAGE' | 'VIDEO' | 'PDF' | 'DOCUMENT' | 'OTHER';
  /** Originals are never served until file checking exists (owner decision 2026-11-02). */
  readonly downloadable: false;
}

export interface ReferenceGroupChip {
  readonly id: string;
  readonly name: string;
}

export interface ReferenceLinkView {
  readonly id: string;
  readonly targetType: ReferenceLinkTargetType;
  readonly targetId: string;
  /** Producer-safe label captured when the link was made (a pin never reads by its real name). */
  readonly label: string;
  /** LIVE, or REMOVED once the item left the production (the link stays, read from the label). */
  readonly state: 'LIVE' | 'REMOVED';
}

/** One row of the library. */
export interface ReferenceCard {
  readonly id: string;
  readonly sourceType: ReferenceSourceType;
  readonly title: string;
  readonly description: string | null;
  /** First lines of a note, for the card. */
  readonly excerpt: string | null;
  readonly category: ReferenceCategory | null;
  readonly url: string | null;
  readonly domain: string | null;
  readonly preview: ReferencePreview;
  readonly file: ReferenceFileFacts | null;
  readonly groups: readonly ReferenceGroupChip[];
  readonly tags: readonly string[];
  /** Only the links whose feature the caller may read. */
  readonly links: readonly ReferenceLinkView[];
  /** Conversations the caller can read; null when conversations are off or not readable. */
  readonly discussionCount: number | null;
  readonly createdBy: ReferencePerson;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly rowVersion: number;
}

export interface ReferenceDetail extends ReferenceCard {
  readonly note: string | null;
  readonly siteName: string | null;
}

export interface ReferenceListPage {
  readonly items: readonly ReferenceCard[];
  readonly nextCursor: string | null;
  /** Matching the filters, across every page. */
  readonly total: number;
}

export interface ReferenceGroupView {
  readonly id: string;
  readonly name: string;
  /** Unique live references in the group. */
  readonly count: number;
}

/** `GET /v1/projects/:projectId/references/meta` — what the sidebar, the chips and the drawer need. */
export interface ReferenceLibraryMeta {
  readonly total: number;
  readonly groups: readonly ReferenceGroupView[];
  readonly counts: {
    readonly byCategory: Readonly<Partial<Record<ReferenceCategory, number>>>;
    readonly bySource: Readonly<Partial<Record<ReferenceSourceType, number>>>;
    readonly linked: number;
    /** Null when conversations are off or the caller cannot read them. */
    readonly discussed: number | null;
  };
  /** Live tags in this production with how many references carry each, most used first. */
  readonly tags: readonly { readonly label: string; readonly count: number }[];
  /** From the backend's own configuration — never a number written into the SPA. */
  readonly upload: {
    readonly maxBytes: number;
    readonly acceptedExtensions: readonly string[];
    /** False on a plan without media storage: the drawer offers the plan instead of the picker. */
    readonly allowed: boolean;
  };
}

/** `POST …/references/link-preview` — never fails the save: every field may be null. */
export interface LinkPreview {
  readonly url: string;
  readonly title: string | null;
  readonly description: string | null;
  readonly siteName: string | null;
  readonly domain: string | null;
  readonly hasImage: boolean;
  /** Why nothing came back, for a quiet hint: BLOCKED | TIMEOUT | NOT_HTML | TOO_LARGE | HTTP_ERROR | UNREACHABLE. */
  readonly failure: string | null;
}

/** `GET …/references/link-targets?type=&q=` — the pickers' options, producer-safe labels. */
export interface ReferenceLinkTargetOption {
  readonly targetType: ReferenceLinkTargetType;
  readonly targetId: string;
  readonly label: string;
  readonly sublabel: string | null;
}
